import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Prospect } from "./ProspectCard";
import { copyText } from "./OpenAndWritePanel";
import {
  DEFAULT_SETTINGS,
  EMPTY_STATS,
  OUTREACH_NETWORKS,
  networkOf,
  type OutreachNetwork,
  addToQueue,
  cooldownRemaining,
  fetchSettings,
  fetchTodayStats,
  removeFromQueue,
  saveSettings,
  setQueueStatus,
  type OutreachSettings,
  type TodayStats,
} from "@/services/sendQueue";
import { seedFrom, varyGreeting } from "@/data/outreach";

type Props = {
  prospects: Prospect[];
  openers: Record<string, string>;
  sent: Record<string, boolean>;
  queueIds: string[];
  buildText: (p: Prospect) => string;
  prepare: (p: Prospect, text: string) => Promise<void>;
  markSent: (p: Prospect) => Promise<void>;
  onQueueChange: (ids: string[]) => void;
  onClose: () => void;
  onToast: (msg: string) => void;
};

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function SendQueuePanel({ prospects, openers, sent, queueIds, buildText, prepare, markSent, onQueueChange, onClose, onToast }: Props) {
  const [settings, setSettings] = useState<OutreachSettings>(DEFAULT_SETTINGS);
  const [draftSettings, setDraftSettings] = useState<OutreachSettings>(DEFAULT_SETTINGS);
  const [stats, setStats] = useState<TodayStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [phase, setPhase] = useState<"ready" | "writing">("ready");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [minScore, setMinScore] = useState(65);
  const [addCount, setAddCount] = useState(10);
  const prepRef = useRef<Promise<void> | null>(null);

  const byId = useMemo(() => new Map(prospects.map((p) => [p.id, p])), [prospects]);
  const [net, setNet] = useState<OutreachNetwork>("instagram");
  const netPicked = useRef(false);
  const allItems = useMemo(() => queueIds.map((id) => byId.get(id)).filter((p): p is Prospect => !!p && !sent[p.id]), [queueIds, byId, sent]);
  const items = useMemo(() => allItems.filter((p) => networkOf(p.platform) === net), [allItems, net]);
  const countByNet = (n: OutreachNetwork) => allItems.filter((p) => networkOf(p.platform) === n).length;
  const current = items[0] ?? null;

  const refreshStats = useCallback(async () => {
    try {
      setStats(await fetchTodayStats());
    } catch {
      /* se mantiene el último valor */
    }
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchSettings(), fetchTodayStats()])
      .then(([s, st]) => {
        if (!alive) return;
        setSettings(s);
        setDraftSettings(s);
        setStats(st);
      })
      .catch(() => onToast("No se pudieron cargar tus ajustes de envío."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Al cambiar el prospecto actual, prepara su texto (con un saludo ligeramente distinto).
  useEffect(() => {
    setPhase("ready");
    if (current) setText(varyGreeting(openers[current.id] ?? buildText(current), seedFrom(current.id)));
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al abrir, se muestra la primera red que tenga prospectos en cola.
  useEffect(() => {
    if (netPicked.current || loading) return;
    netPicked.current = true;
    const first = OUTREACH_NETWORKS.find((n) => allItems.some((p) => networkOf(p.platform) === n.value));
    if (first) setNet(first.value);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const cap = settings.caps[net];
  const netStats = stats[net];
  const remainingCap = Math.max(0, cap - netStats.sentToday);
  const cooldown = cooldownRemaining(netStats.lastSentAt, settings.pauseSeconds, now);
  const capped = remainingCap <= 0;
  const waiting = phase === "ready" && cooldown > 0;

  const openAndCopy = () => {
    if (!current || capped || waiting) return;
    if (!current.profileUrl) {
      onToast("Este prospecto no tiene enlace de perfil. Sáltalo.");
      return;
    }
    // Copiar y abrir dentro del mismo toque para que el navegador no lo bloquee.
    void copyText(text).then((ok) => onToast(ok ? "Mensaje copiado. Pégalo en el chat." : "No se pudo copiar: selecciona el texto y cópialo."));
    window.open(current.profileUrl, "_blank", "noopener,noreferrer");
    setPhase("writing");
    prepRef.current = prepare(current, text).catch((e) => onToast(e instanceof Error ? `No se pudo guardar el mensaje: ${e.message}` : "No se pudo guardar el mensaje."));
  };

  const confirmSent = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await prepRef.current;
      await markSent(current);
      await setQueueStatus(current.id, "done");
      onQueueChange(queueIds.filter((id) => id !== current.id));
      await refreshStats();
      setPhase("ready");
      onToast("Marcado como enviado");
    } catch (e) {
      onToast(e instanceof Error ? `No se pudo guardar el envío: ${e.message}` : "No se pudo guardar el envío.");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await setQueueStatus(current.id, "skipped");
      onQueueChange(queueIds.filter((id) => id !== current.id));
    } catch (e) {
      onToast(e instanceof Error ? e.message : "No se pudo saltar.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await removeFromQueue(id);
      onQueueChange(queueIds.filter((x) => x !== id));
    } catch (e) {
      onToast(e instanceof Error ? e.message : "No se pudo quitar.");
    }
  };

  const addBest = async () => {
    const inQueue = new Set(queueIds);
    const picks = prospects
      .filter((p) => networkOf(p.platform) === net && p.profileUrl && p.status === "Nuevo" && !sent[p.id] && !inQueue.has(p.id) && p.score >= minScore)
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.max(1, Math.min(100, addCount)));
    if (picks.length === 0) {
      onToast("No hay prospectos nuevos de esta red con ese Score para agregar.");
      return;
    }
    try {
      await addToQueue(picks.map((p) => p.id));
      onQueueChange([...queueIds, ...picks.map((p) => p.id)]);
      onToast(`${picks.length} prospectos agregados a la cola`);
    } catch (e) {
      onToast(e instanceof Error ? `No se pudo agregar: ${e.message}` : "No se pudo agregar a la cola.");
    }
  };

  const saveCfg = async () => {
    try {
      const s = await saveSettings(draftSettings);
      setSettings(s);
      setDraftSettings(s);
      setShowSettings(false);
      onToast("Ajustes guardados");
    } catch (e) {
      onToast(e instanceof Error ? `No se pudo guardar: ${e.message}` : "No se pudieron guardar los ajustes.");
    }
  };
  const pct = Math.min(100, Math.round((netStats.sentToday / Math.max(1, cap)) * 100));
  const upcoming = items.slice(1, 6);

  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/50" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="bg-background-50 w-full md:max-w-xl max-h-[92vh] md:max-h-[88vh] rounded-t-3xl md:rounded-3xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-4 border-b border-background-200">
          <span className="w-10 h-10 rounded-xl bg-primary-500/15 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-play-list-add-line text-xl"></i>
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-foreground-950">Cola de envío</h3>
            <p className="text-xs text-foreground-500">Envías tú, de a uno, con tope diario y pausa entre mensajes.</p>
          </div>
          <button onClick={() => setShowSettings((v) => !v)} aria-label="Ajustes" className="w-9 h-9 rounded-full hover:bg-background-200 flex items-center justify-center cursor-pointer">
            <i className="ri-settings-3-line text-lg"></i>
          </button>
          <button onClick={onClose} aria-label="Cerrar" className="w-9 h-9 rounded-full hover:bg-background-200 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {OUTREACH_NETWORKS.map((n) => {
              const active = n.value === net;
              return (
                <button
                  key={n.value}
                  onClick={() => {
                    setNet(n.value);
                    setPhase("ready");
                  }}
                  className={`rounded-xl border px-2 py-2 text-center cursor-pointer whitespace-nowrap ${active ? "border-primary-500 bg-primary-500/10" : "border-background-200 bg-background-100 hover:bg-background-200"}`}
                >
                  <i className={`${n.icon} text-lg ${active ? "text-primary-700" : "text-foreground-500"}`}></i>
                  <p className="text-xs font-semibold text-foreground-900">{n.label}</p>
                  <p className="text-[11px] text-foreground-500 tabular-nums">
                    {stats[n.value].sentToday}/{settings.caps[n.value]} · {countByNet(n.value)} en cola
                  </p>
                </button>
              );
            })}
          </div>
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-foreground-900">
                Hoy en {OUTREACH_NETWORKS.find((n) => n.value === net)?.label}: {netStats.sentToday} de {cap}
              </span>
              <span className="text-xs text-foreground-500">{capped ? "Tope alcanzado" : `Te quedan ${remainingCap}`}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
              <div className={`h-full ${capped ? "bg-accent-500" : "bg-primary-500"}`} style={{ width: `${pct}%` }}></div>
            </div>
          </div>
          {showSettings ? (
            <div className="rounded-2xl border border-background-200 bg-background-100 p-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {OUTREACH_NETWORKS.map((n) => (
                  <label key={n.value} className="text-xs font-semibold text-foreground-700">
                    Tope diario · {n.label}
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={draftSettings.caps[n.value]}
                      onChange={(e) => setDraftSettings((s) => ({ ...s, caps: { ...s.caps, [n.value]: Number(e.target.value) } }))}
                      className="mt-1 w-full bg-background-50 border border-background-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-400"
                    />
                  </label>
                ))}
                <label className="text-xs font-semibold text-foreground-700">
                  Pausa (segundos)
                  <input
                    type="number"
                    min={30}
                    max={600}
                    value={draftSettings.pauseSeconds}
                    onChange={(e) => setDraftSettings((s) => ({ ...s, pauseSeconds: Number(e.target.value) }))}
                    className="mt-1 w-full bg-background-50 border border-background-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-400"
                  />
                </label>
              </div>              <p className="text-[11px] text-foreground-500">
                La meta es de 100 mensajes por red al día. Las redes no publican un número seguro: si una te limita o te pide verificar, baja el tope de esa red. Cada red lleva su propio conteo y su propia pausa, y la pausa varía un poco entre mensajes.
              </p>
              {Math.max(...Object.values(draftSettings.caps)) > 100 ? (
                <p className="text-[11px] font-semibold text-accent-700">Más de 100 al día aumenta el riesgo de que la red limite tu cuenta.</p>
              ) : null}
              <button onClick={() => void saveCfg()} className="w-full py-2 rounded-xl bg-primary-500 text-background-950 font-heading font-bold text-sm cursor-pointer whitespace-nowrap">
                Guardar ajustes
              </button>
            </div>
          ) : null}
          {loading ? (
            <p className="text-center text-sm text-foreground-500 py-6">
              <i className="ri-loader-4-line animate-spin"></i> Cargando…
            </p>
          ) : capped ? (
            <div className="rounded-2xl border border-accent-500/30 bg-accent-500/5 p-4 text-center">
              <i className="ri-shield-check-line text-2xl text-accent-700"></i>
              <p className="font-semibold text-foreground-900 mt-1">Llegaste al tope de hoy en esta red</p>
              <p className="text-sm text-foreground-600 mt-1">Sigue con otra red o continúa mañana. Puedes ajustar el tope en el engranaje, con cuidado.</p>
            </div>
          ) : !current ? (
            <div className="rounded-2xl border border-dashed border-background-300 p-4 text-center">
              <p className="font-semibold text-foreground-900">La cola está vacía</p>
              <p className="text-sm text-foreground-600 mt-1">Agrega prospectos de esta red desde las tarjetas o con "Agregar mejores", aquí abajo.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-background-200 bg-background-100 p-3">
              <div className="flex items-center gap-3">
                <img src={current.photo} alt="" className="w-11 h-11 rounded-xl object-cover object-top flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-bold text-foreground-950 truncate">{current.name}</p>
                  <p className="text-xs text-foreground-500 truncate">
                    {current.platform} · {current.username} · Score {current.score}
                  </p>
                </div>
                <span className="text-[11px] text-foreground-500 whitespace-nowrap">{items.length} en cola</span>
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                disabled={phase === "writing"}
                className="mt-3 w-full text-sm bg-background-50 border border-background-200 rounded-xl p-2.5 outline-none focus:border-primary-400 resize-y disabled:opacity-70"
              />
              {phase === "ready" ? (
                waiting ? (
                  <div className="mt-3 rounded-xl bg-background-200 p-3 text-center">
                    <p className="text-xs text-foreground-600">Pausa antes del siguiente</p>
                    <p className="font-heading font-bold text-2xl text-foreground-950 tabular-nums">{fmt(cooldown)}</p>
                  </div>
                ) : (
                  <button
                    onClick={openAndCopy}
                    className="mt-3 w-full py-3 rounded-xl bg-primary-500 text-background-950 font-heading font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:bg-primary-400"
                  >
                    <i className="ri-chat-new-line"></i> Abrir perfil y copiar mensaje
                  </button>
                )
              ) : (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-foreground-600">
                    En la pestaña que se abrió toca Mensaje, pega (Ctrl+V) y envía. Vuelve aquí y confirma.
                  </p>
                  <button
                    onClick={() => void confirmSent()}
                    disabled={busy}
                    className="w-full py-3 rounded-xl bg-secondary-500 text-white font-heading font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-fill"></i>} Ya lo envié
                  </button>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => void copyText(text).then((ok) => onToast(ok ? "Mensaje copiado" : "No se pudo copiar"))} className="py-2 rounded-xl border border-background-300 text-xs font-semibold cursor-pointer whitespace-nowrap">
                      Copiar de nuevo
                    </button>
                    <button onClick={() => current.profileUrl && window.open(current.profileUrl, "_blank", "noopener,noreferrer")} className="py-2 rounded-xl border border-background-300 text-xs font-semibold cursor-pointer whitespace-nowrap">
                      Abrir perfil
                    </button>
                    <button onClick={() => setPhase("ready")} className="py-2 rounded-xl border border-background-300 text-xs font-semibold cursor-pointer whitespace-nowrap">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {phase === "ready" ? (
                <button onClick={() => void skip()} disabled={busy} className="mt-2 w-full text-xs font-semibold text-foreground-500 hover:text-foreground-800 cursor-pointer py-1.5">
                  Saltar este prospecto
                </button>
              ) : null}
            </div>
          )}
          {upcoming.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-foreground-600 mb-1.5">Siguientes</p>
              <ul className="space-y-1.5">
                {upcoming.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-xl px-3 py-2">
                    <span className="text-sm text-foreground-800 truncate flex-1">{p.name}</span>
                    <span className="text-[11px] text-foreground-500">{p.score}</span>
                    <button onClick={() => void remove(p.id)} aria-label={`Quitar a ${p.name}`} className="text-foreground-400 hover:text-accent-700 cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="rounded-2xl border border-background-200 p-3">
            <p className="text-xs font-semibold text-foreground-700 mb-2">Agregar mejores prospectos nuevos</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] text-foreground-600">
                Score mínimo
                <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="mt-1 w-full bg-background-50 border border-background-200 rounded-xl px-2 py-2 text-sm">
                  <option value={80}>80 o más</option>
                  <option value={65}>65 o más</option>
                  <option value={50}>50 o más</option>
                  <option value={0}>Todos</option>
                </select>
              </label>
              <label className="text-[11px] text-foreground-600">
                Cantidad
                <input type="number" min={1} max={100} value={addCount} onChange={(e) => setAddCount(Number(e.target.value))} className="mt-1 w-full bg-background-50 border border-background-200 rounded-xl px-2 py-2 text-sm" />
              </label>
            </div>
            <button onClick={() => void addBest()} className="mt-2 w-full py-2 rounded-xl border border-primary-500/40 text-primary-700 bg-primary-500/5 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-500/15">
              <i className="ri-add-line"></i> Agregar a la cola
            </button>
            <p className="text-[10px] text-foreground-500 mt-2">Vívelo no envía nada solo: cada mensaje lo pegas y lo envías tú desde tu cuenta.</p>
          </div>
        </div>
      </div>
    </div>
  );
}